/**
 * Fold the flat, ACL-filtered location list into a render tree.
 *
 * The backend returns an adjacency list (`parent_id` per row), not a nested
 * structure, and it filters per-row by ACL. So a node whose parent is NOT in the
 * visible set (the admin can see the child unit but not its parent) must still
 * render — it surfaces as a **pseudo-root**. Treating an unresolved `parent_id`
 * as "no parent" is the whole reason this is a deliberate function and not an
 * inline reduce: silently dropping such nodes would hide units the admin is
 * entitled to manage.
 */

import type { Location } from '@/api/endpoints/org';

export interface OrgTreeNode {
  location: Location;
  children: OrgTreeNode[];
  depth: number;
  /** True when parent_id is set but the parent is outside the visible set. */
  orphan: boolean;
}

function byName(a: OrgTreeNode, b: OrgTreeNode): number {
  return a.location.name.localeCompare(b.location.name);
}

export function buildTree(locations: Location[]): OrgTreeNode[] {
  const nodes = new Map<string, OrgTreeNode>();
  for (const location of locations) {
    nodes.set(location.id, { location, children: [], depth: 0, orphan: false });
  }

  const roots: OrgTreeNode[] = [];
  for (const node of nodes.values()) {
    const parentId = node.location.parent_id;
    const parent = parentId ? nodes.get(parentId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      // No parent_id → a genuine root. parent_id set but not visible → orphan
      // pseudo-root (parent hidden by ACL). Either way it renders at top level.
      node.orphan = !!parentId;
      roots.push(node);
    }
  }

  // Stable, name-sorted order + correct depth for indentation.
  const sortAndDepth = (list: OrgTreeNode[], depth: number): void => {
    list.sort(byName);
    for (const n of list) {
      n.depth = depth;
      sortAndDepth(n.children, depth + 1);
    }
  };
  sortAndDepth(roots, 0);
  return roots;
}

/**
 * Roll the per-node DIRECT patient counts up the tree: each node's value is its
 * own occupancy plus every descendant's. The rollup runs over the tree the
 * console already assembled, which holds ONLY the units the caller may see (the
 * backend ACL-filters `GET /locations` per row). So the census can never count —
 * or leak — occupancy of a unit outside the caller's access: a filtered-out node
 * simply is not in the tree, and contributes nothing. Returns a map of location
 * id → rolled-up count, computed in one O(n) post-order walk.
 */
export function rollUpPatientCounts(roots: OrgTreeNode[]): Map<string, number> {
  const totals = new Map<string, number>();
  const visit = (n: OrgTreeNode): number => {
    let total = n.location.direct_patient_count ?? 0;
    for (const child of n.children) total += visit(child);
    totals.set(n.location.id, total);
    return total;
  };
  roots.forEach(visit);
  return totals;
}

/** Flatten the tree to a list honouring an expanded-id set (for keyboard nav / filtering). */
export function flattenVisible(roots: OrgTreeNode[], expanded: Set<string>): OrgTreeNode[] {
  const out: OrgTreeNode[] = [];
  const walk = (n: OrgTreeNode): void => {
    out.push(n);
    if (expanded.has(n.location.id)) n.children.forEach(walk);
  };
  roots.forEach(walk);
  return out;
}

/**
 * All descendant ids of a unit, computed from the flat (ACL-filtered) list — the
 * down-tree counterpart to the breadcrumb's ancestor chain. Used to aggregate a
 * parent's staffing across its whole subtree. Only units present in the list (i.e.
 * visible to the caller) are walked, so the set never reaches a unit outside
 * access. Excludes the root itself.
 *
 * A `visited` set guards against corrupt `parent_id` cycles in the data — the
 * backend's recursive CTE carries a CYCLE clause for the same reason (a row can
 * point at an ancestor as its parent). Without it a cycle (A.parent=B, B.parent=A)
 * would spin this loop forever and hang the tab; the set also dedupes a unit
 * reachable by two paths.
 */
export function collectDescendantIds(locations: Location[], rootId: string): string[] {
  const childrenByParent = new Map<string, string[]>();
  for (const l of locations) {
    if (!l.parent_id) continue;
    const siblings = childrenByParent.get(l.parent_id) ?? [];
    siblings.push(l.id);
    childrenByParent.set(l.parent_id, siblings);
  }
  const out: string[] = [];
  const visited = new Set<string>([rootId]);
  const stack = [...(childrenByParent.get(rootId) ?? [])];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    if (visited.has(id)) continue;
    visited.add(id);
    out.push(id);
    const kids = childrenByParent.get(id);
    if (kids) stack.push(...kids);
  }
  return out;
}

/** All descendant ids of a node (used to block self/descendant as a move target). */
export function descendantIds(node: OrgTreeNode): Set<string> {
  const ids = new Set<string>();
  const walk = (n: OrgTreeNode): void => {
    for (const c of n.children) {
      ids.add(c.location.id);
      walk(c);
    }
  };
  walk(node);
  return ids;
}
